/**
 * Pone el ícono del tema adentro del botón de transferencia de la app **Scala Discounts**.
 *
 * ## Por qué hace falta JS para esto
 *
 * Ese botón no lo dibuja el tema: lo inyecta la app como HERMANO de `.cart__ctas`, y su contenido
 * vive en un **shadow root**, adonde no llegan ni el CSS ni el Liquid de este tema. Hasta acá eso
 * obligaba a dejarlo como venía, con el emoji 🏦 que la app escribe dentro de su texto.
 *
 * Medido en vivo sobre `scala-cart-button`, el shadow root resultó **abierto** y con slots:
 *
 *     <button part="actualbutton">
 *       <slot name="icon"  part="icon" aria-hidden="true"></slot>   <-- VACÍO
 *       <slot name="message" part="message"> 🏦 Transferencia — 10% OFF </slot>
 *     </button>
 *
 * El slot `icon` está vacío y es un slot NOMBRADO, así que llenarlo es la API estándar de Web
 * Components: se le cuelga al elemento un hijo con `slot="icon"` y el navegador lo proyecta
 * adentro. No se toca el shadow root, no se parchea la app y no se pelea con sus estilos.
 *
 * Si algún día la app renombra ese slot, el hijo simplemente deja de renderizarse y el botón
 * vuelve a verse como hoy. Degrada a lo anterior, no a algo roto.
 *
 * ## Por qué mira si ya hay un emoji
 *
 * El 🏦 no está en el slot del ícono: viaja DENTRO del texto del mensaje, que se configura en la
 * app. Si se planta el ícono sin sacar ese emoji, quedan los dos. Por eso acá se lee el mensaje y,
 * si todavía trae un pictograma, no se hace nada.
 *
 * Eso vuelve el cambio auto-activable: se sube esto, no pasa nada visible, y el día que se borre
 * el emoji del texto en el panel de la app el ícono aparece solo. Sin coordinar un despliegue con
 * una edición del Admin, que es donde se rompen estas cosas.
 *
 * ## Por qué observa el DOM
 *
 * Dos motivos: la app monta su botón DESPUÉS de este script, y el carrito se vuelve a renderizar
 * entero en cada cambio de cantidad (`morphSection`), así que el botón puede aparecer, irse y
 * volver muchas veces en la misma página. La observación se agrupa por cuadro para no reaccionar
 * a cada mutación suelta — plantar el ícono ES una mutación, y sin el agrupado se realimenta.
 */

const SLOT = 'icon';
const PICTOGRAMA = /\p{Extended_Pictographic}/u;

class ScalaIconSlot extends HTMLElement {
  #observador = null;
  #pedido = 0;

  connectedCallback() {
    this.#plantarPronto();

    this.#observador = new MutationObserver(() => this.#plantarPronto());
    this.#observador.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observador?.disconnect();
    this.#observador = null;
    cancelAnimationFrame(this.#pedido);
  }

  #plantarPronto() {
    cancelAnimationFrame(this.#pedido);
    this.#pedido = requestAnimationFrame(() => this.#plantar());
  }

  #plantar() {
    const molde = this.querySelector('template');
    if (!molde) return;

    for (const boton of document.querySelectorAll('scala-cart-button')) {
      // Ya tiene el nuestro.
      if (boton.querySelector(`[slot="${SLOT}"]`)) continue;

      // El shadow root puede no estar montado todavía en el primer cuadro; la próxima mutación
      // vuelve a pasar por acá, así que no hace falta reintentar a mano.
      const raiz = boton.shadowRoot;
      if (!raiz) continue;

      // Con emoji en el texto no se planta nada: si no, quedarían los dos. Ver el bloque de arriba.
      const mensaje = raiz.querySelector('[part="message"]');
      if (!mensaje || PICTOGRAMA.test(mensaje.textContent ?? '')) continue;

      boton.appendChild(molde.content.cloneNode(true));
    }
  }
}

if (!customElements.get('scala-icon-slot')) {
  customElements.define('scala-icon-slot', ScalaIconSlot);
}
